// Hash-based routing for the dashboard: fixed tabs + a dynamic province route.
// The province route (`#tinh/79`) is a virtual view rendered in the tab-host;
// currentTab() stays as a compatibility wrapper (returns the last real tab) so
// consumers that only care about tabs don't need to change.

export const TABS = ["highlight", "kinh-te", "chinh-tri-xa-hoi", "bat-dong-san", "ban-do-3d"] as const;
export type Tab = (typeof TABS)[number];

export const TAB_LABELS: Record<Tab, string> = {
  highlight: "🇻🇳 Tổng quan",
  "kinh-te": "Kinh tế",
  "chinh-tri-xa-hoi": "Chính trị – Xã hội",
  "bat-dong-san": "Bất động sản",
  "ban-do-3d": "🗺 Bản đồ 3D",
};

// Old shared/deep-linked hashes map to their new home. Phân tích folded into
// Tổng quan; Chính trị + Xã hội merged into one tab.
const LEGACY_TAB_REDIRECTS: Record<string, Tab> = {
  "chinh-tri": "chinh-tri-xa-hoi",
  "xa-hoi": "chinh-tri-xa-hoi",
  "phan-tich": "highlight",
};

// Highlight (Tổng quan) is the default landing tab.
export const DEFAULT_TAB: Tab = "highlight";

export type Route = { kind: "tab"; tab: Tab } | { kind: "province"; code: string };

// Last resolved real tab — gives the province view a sensible "back" target and
// keeps nav active-state coherent while on a province route.
let lastTab: Tab = DEFAULT_TAB;

// PURE resolver — reads the hash and returns a route WITHOUT mutating location.
// Legacy hashes resolve to their target tab here; the URL is normalized once,
// separately, in normalizeLegacyHash (called from init + the hashchange path).
export function currentRoute(): Route {
  const hash = window.location.hash.replace(/^#/, "");
  const m = /^tinh\/(\d{1,3})$/.exec(hash);
  if (m) return { kind: "province", code: m[1] };
  // Shareable 3D deep-link (#ban-do-3d/48): resolves to the 3D tab; the tab's panel
  // reads the code and opens that province's detail stage itself.
  if (/^ban-do-3d\/\d{1,3}$/.test(hash)) {
    lastTab = "ban-do-3d";
    return { kind: "tab", tab: "ban-do-3d" };
  }
  const legacy = LEGACY_TAB_REDIRECTS[hash];
  if (legacy) {
    lastTab = legacy;
    return { kind: "tab", tab: legacy };
  }
  if ((TABS as readonly string[]).includes(hash)) {
    lastTab = hash as Tab;
    return { kind: "tab", tab: hash as Tab };
  }
  return { kind: "tab", tab: DEFAULT_TAB };
}

// Rewrite a legacy hash in the URL to its target, WITHOUT pushing a history
// entry (replaceState, not `location.hash =`) and WITHOUT firing hashchange.
// Returns true if it rewrote — callers can skip the redundant re-route.
export function normalizeLegacyHash(): boolean {
  const hash = window.location.hash.replace(/^#/, "");
  const target = LEGACY_TAB_REDIRECTS[hash];
  if (!target) return false;
  window.history.replaceState(null, "", `#${target}`);
  return true;
}

// Compatibility wrapper: the currently-active real tab.
export function currentTab(): Tab {
  const r = currentRoute();
  return r.kind === "tab" ? r.tab : lastTab;
}

// Fires on any hash change with the resolved route (tab or province).
export function onRouteChange(cb: (route: Route) => void): void {
  window.addEventListener("hashchange", () => cb(currentRoute()));
}

// Retained for existing consumers that only care about tab changes.
export function onTabChange(cb: (tab: Tab) => void): void {
  window.addEventListener("hashchange", () => {
    const r = currentRoute();
    if (r.kind === "tab") cb(r.tab);
  });
}

export function goToTab(tab: Tab): void {
  window.location.hash = `#${tab}`;
}

export function goToProvince(code: string): void {
  window.location.hash = `#tinh/${code}`;
}
